import { Dialog } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';
import { useState } from 'react';

import * as styles from './OverflowPanel.css';

interface OverflowPanelProps {
  children: ReactNode;
  className?: string;
  dialogContent: ReactNode;
  dialogDescription: string;
  dialogTitle: string;
  hasOverflow: boolean;
  overflowAriaLabel: string;
  overflowText: string;
}

/**
 * Generic overflow panel component that handles content overflow with dialog expansion
 * Provides accessibility features including ARIA attributes, keyboard support, and screen reader compatibility
 */
export function OverflowPanel({
  children,
  className,
  dialogContent,
  dialogDescription,
  dialogTitle,
  hasOverflow,
  overflowAriaLabel,
  overflowText,
}: OverflowPanelProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const containerClasses = [styles.overflowContainer, className, hasOverflow ? styles.clickablePanel : ''].filter(Boolean).join(' ');

  const handleOpenDialog = () => {
    if (hasOverflow) {
      setIsDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  if (!hasOverflow) {
    return (
      <div className={containerClasses}>
        <div className={styles.contentContainer}>{children}</div>
      </div>
    );
  }

  return (
    <>
      <div
        aria-expanded={isDialogOpen}
        aria-haspopup="dialog"
        aria-label={overflowAriaLabel}
        className={containerClasses}
        role="button"
        tabIndex={0}
        onClick={handleOpenDialog}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpenDialog();
          }
        }}
      >
        <div className={styles.contentContainer}>{children}</div>
        <div aria-hidden="true" className={styles.overflowIndicator}>
          {overflowText}
        </div>
        <span className={styles.srOnly}>Press Enter or Space to view all items in a dialog</span>
      </div>

      <Dialog description={dialogDescription} isOpen={isDialogOpen} size="lg" title={dialogTitle} onClose={handleCloseDialog}>
        <div className={styles.dialogContent}>{dialogContent}</div>
      </Dialog>
    </>
  );
}
